/**
 * 支付通道管理组件
 * 用于管理员配置和切换不同的支付通道（如ZPAY等）
 */

import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { paymentChannelsApi } from '../../services/adminApi'

interface PaymentChannel {
  id: number
  channel_code: string
  channel_name: string
  channel_type: string
  config: any  // 根据通道类型不同，配置字段不同
  config_display?: any
  supported_methods: string[]
  api_url: string
  is_active: number
  is_default: number
  priority: number
  fee_rate: number
  min_amount: number
  max_amount: number
  description: string
  admin_notes: string
  created_at: string
  updated_at: string
}

export default function PaymentChannelsManagement() {
  const [channels, setChannels] = useState<PaymentChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editingChannel, setEditingChannel] = useState<PaymentChannel | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  
  // 编辑表单状态（通用字段）
  const [formData, setFormData] = useState<any>({
    channel_name: '',
    api_url: '',
    is_active: false,
    is_default: false,
    description: ''
  })
  
  // 加载支付通道列表
  const loadChannels = async () => {
    setLoading(true)
    try {
      const response = await paymentChannelsApi.getChannels()
      if (response.success) {
        setChannels(response.data || [])
      } else {
        toast.error('加载支付通道失败')
      }
    } catch (error) {
      console.error('加载支付通道失败:', error)
      toast.error('加载支付通道失败')
    } finally {
      setLoading(false)
    }
  }
  
  useEffect(() => {
    loadChannels()
  }, [])
  
  // 打开编辑弹窗
  const handleEdit = async (channel: PaymentChannel) => {
    try {
      // 获取完整配置
      const response = await paymentChannelsApi.getChannelById(channel.id)
      if (response.success && response.data) {
        const fullChannel = response.data
        setEditingChannel(fullChannel)
        
        // 根据通道类型设置表单数据
        const config = fullChannel.config || {}
        const baseData = {
          channel_name: fullChannel.channel_name || '',
          api_url: fullChannel.api_url || '',
          is_active: !!fullChannel.is_active,
          is_default: !!fullChannel.is_default,
          description: fullChannel.description || ''
        }
        
        // 根据通道代码设置不同的配置字段
        if (fullChannel.channel_code === 'zpay') {
          setFormData({
            ...baseData,
            pid: config.pid || '',
            key: config.key || ''
          })
        } else if (fullChannel.channel_code === 'wechat_official') {
          setFormData({
            ...baseData,
            mch_id: config.mch_id || '',
            api_key: config.api_key || '',
            app_id: config.app_id || '',
            cert_serial_no: config.cert_serial_no || '',
            private_key: config.private_key || ''
          })
        } else if (fullChannel.channel_code === 'alipay_official') {
          setFormData({
            ...baseData,
            app_id: config.app_id || '',
            private_key: config.private_key || '',
            alipay_public_key: config.alipay_public_key || ''
          })
        } else {
          setFormData(baseData)
        }
        
        setShowEditModal(true)
      }
    } catch (error) {
      toast.error('获取通道详情失败')
    }
  }
  
  // 保存配置
  const handleSave = async () => {
    if (!editingChannel) return
    
    setSaving(true)
    try {
      // 根据通道类型构建不同的config对象
      let config: any = {}
      
      if (editingChannel.channel_code === 'zpay') {
        // ZPAY配置校验
        if (!formData.pid || !formData.key) {
          toast.error('请填写完整的ZPAY配置信息')
          setSaving(false)
          return
        }
        config = {
          pid: formData.pid,
          key: formData.key
        }
      } else if (editingChannel.channel_code === 'wechat_official') {
        // 微信官方配置校验
        if (!formData.mch_id || !formData.api_key || !formData.app_id || !formData.cert_serial_no || !formData.private_key) {
          toast.error('请填写完整的微信支付配置信息')
          setSaving(false)
          return
        }
        config = {
          mch_id: formData.mch_id,
          api_key: formData.api_key,
          app_id: formData.app_id,
          cert_serial_no: formData.cert_serial_no,
          private_key: formData.private_key
        }
      } else if (editingChannel.channel_code === 'alipay_official') {
        // 支付宝官方配置校验
        if (!formData.app_id || !formData.private_key || !formData.alipay_public_key) {
          toast.error('请填写完整的支付宝配置信息')
          setSaving(false)
          return
        }
        config = {
          app_id: formData.app_id,
          private_key: formData.private_key,
          alipay_public_key: formData.alipay_public_key,
          charset: 'utf-8',
          sign_type: 'RSA2'
        }
      }
      
      const response = await paymentChannelsApi.updateChannel(editingChannel.id, {
        channel_name: formData.channel_name,
        config: config,
        api_url: formData.api_url,
        is_active: formData.is_active,
        is_default: formData.is_default,
        description: formData.description
      })
      
      if (response.success) {
        toast.success('保存成功')
        setShowEditModal(false)
        loadChannels()
      } else {
        toast.error(response.error?.message || '保存失败')
      }
    } catch (error) {
      toast.error('保存失败')
    } finally {
      setSaving(false)
    }
  }
  
  // 切换启用状态
  const handleToggle = async (channel: PaymentChannel) => {
    try {
      const response = await paymentChannelsApi.toggleChannel(channel.id, !channel.is_active)
      if (response.success) {
        toast.success(channel.is_active ? '已禁用' : '已启用')
        loadChannels()
      } else {
        toast.error(response.error?.message || '操作失败')
      }
    } catch (error) {
      toast.error('操作失败')
    }
  }
  
  // 设置默认
  const handleSetDefault = async (channel: PaymentChannel) => {
    if (!channel.is_active) {
      toast.error('请先启用该通道')
      return
    }
    
    try {
      const response = await paymentChannelsApi.setDefaultChannel(channel.id)
      if (response.success) {
        toast.success('已设为默认')
        loadChannels()
      } else {
        toast.error(response.error?.message || '操作失败')
      }
    } catch (error) {
      toast.error('操作失败')
    }
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* 说明信息 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <span className="text-blue-600 mr-3">ℹ️</span>
          <div>
            <h4 className="font-medium text-blue-900">支付通道配置说明</h4>
            <ul className="mt-2 text-sm text-blue-700 space-y-1">
              <li>• 支持三种支付通道：ZPAY聚合、微信官方、支付宝官方</li>
              <li>• ZPAY聚合支付：通过第三方接入，配置简单，无需认证</li>
              <li>• 微信官方支付：需要商户号、API密钥、证书序列号等</li>
              <li>• 支付宝官方支付：需要应用ID、应用私钥、支付宝公钥等</li>
              <li>• 可同时启用多个通道，系统会根据优先级自动选择</li>
              <li>• 请确保配置信息正确，否则支付将无法完成</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* 支付通道列表 */}
      <div className="space-y-4">
        {channels.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            暂无支付通道，请联系技术人员添加
          </div>
        ) : (
          channels.map((channel) => (
            <div
              key={channel.id}
              className={`border rounded-lg p-4 ${
                channel.is_active ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* 状态图标 */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    channel.is_active ? 'bg-green-500' : 'bg-gray-400'
                  }`}>
                    <span className="text-white text-xl">💳</span>
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900">{channel.channel_name}</h3>
                      {channel.is_default ? (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                          默认
                        </span>
                      ) : null}
                      {channel.is_active ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          已启用
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          已禁用
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {channel.description || `${channel.channel_type === 'third_party' ? '第三方' : '官方'}支付通道`}
                    </p>
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                      <span>通道代码: {channel.channel_code}</span>
                      <span>商户ID: {channel.config_display?.pid || '未配置'}</span>
                      <span>支持: {(channel.supported_methods || []).map(m => m === 'wxpay' ? '微信支付' : m === 'alipay' ? '支付宝' : m).join('、')}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {!channel.is_default && channel.is_active && (
                    <button
                      onClick={() => handleSetDefault(channel)}
                      className="px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    >
                      设为默认
                    </button>
                  )}
                  <button
                    onClick={() => handleToggle(channel)}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      channel.is_active
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                  >
                    {channel.is_active ? '禁用' : '启用'}
                  </button>
                  <button
                    onClick={() => handleEdit(channel)}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    配置
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* 编辑弹窗 */}
      {showEditModal && editingChannel && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 text-white">
              <h3 className="font-bold text-lg">配置支付通道</h3>
              <p className="text-purple-200 text-sm mt-1">{editingChannel.channel_name}</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* 通道名称 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  通道名称
                </label>
                <input
                  type="text"
                  value={formData.channel_name}
                  onChange={(e) => setFormData({ ...formData, channel_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="支付通道名称"
                />
              </div>
              
              {/* 根据通道类型渲染不同的配置字段 */}
              {editingChannel.channel_code === 'zpay' && (
                <>
                  {/* 商户ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      商户ID (PID) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.pid || ''}
                      onChange={(e) => setFormData({ ...formData, pid: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="请输入商户ID"
                    />
                  </div>
                  
                  {/* 商户密钥 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      商户密钥 (KEY) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.key || ''}
                        onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="请输入商户密钥"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? '👁️' : '🙈'}
                      </button>
                    </div>
                  </div>
                </>
              )}
              
              {editingChannel.channel_code === 'wechat_official' && (
                <>
                  {/* 微信商户号 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      微信商户号 (mch_id) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.mch_id || ''}
                      onChange={(e) => setFormData({ ...formData, mch_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="请输入微信商户号"
                    />
                  </div>
                  
                  {/* 微信应用ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      微信应用ID (app_id) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.app_id || ''}
                      onChange={(e) => setFormData({ ...formData, app_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="请输入微信应用ID"
                    />
                  </div>
                  
                  {/* API密钥v3 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API密钥v3 (api_key) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.api_key || ''}
                        onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="32位API密钥"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? '👁️' : '🙈'}
                      </button>
                    </div>
                  </div>
                  
                  {/* 证书序列号 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      证书序列号 (cert_serial_no) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.cert_serial_no || ''}
                      onChange={(e) => setFormData({ ...formData, cert_serial_no: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="商户证书序列号"
                    />
                  </div>
                  
                  {/* 商户私钥 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      商户私钥 (private_key) <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.private_key || ''}
                      onChange={(e) => setFormData({ ...formData, private_key: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-xs"
                      placeholder="-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      请粘贴完整的PEM格式私钥内容
                    </p>
                  </div>
                </>
              )}
              
              {editingChannel.channel_code === 'alipay_official' && (
                <>
                  {/* 支付宝应用ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      支付宝应用ID (app_id) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.app_id || ''}
                      onChange={(e) => setFormData({ ...formData, app_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="请输入支付宝应用ID"
                    />
                  </div>
                  
                  {/* 应用私钥 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      应用私钥 (private_key) <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.private_key || ''}
                      onChange={(e) => setFormData({ ...formData, private_key: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-xs"
                      placeholder="应用私钥（不含-----BEGIN/END-----）"
                    />
                  </div>
                  
                  {/* 支付宝公钥 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      支付宝公钥 (alipay_public_key) <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.alipay_public_key || ''}
                      onChange={(e) => setFormData({ ...formData, alipay_public_key: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-xs"
                      placeholder="支付宝公钥（不含-----BEGIN/END-----）"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      从支付宝开放平台获取
                    </p>
                  </div>
                </>
              )}
              
              {/* API地址 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API地址
                </label>
                <input
                  type="text"
                  value={formData.api_url}
                  onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="https://zpayz.cn"
                />
              </div>
              
              {/* 描述 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="通道描述信息"
                />
              </div>
              
              {/* 开关选项 */}
              <div className="flex items-center space-x-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">启用通道</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">设为默认</span>
                </label>
              </div>
              
              {/* 安全提示 */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start">
                  <span className="text-amber-600 mr-2">⚠️</span>
                  <p className="text-sm text-amber-700">
                    请妥善保管商户密钥，不要泄露给他人。如密钥泄露，请立即在支付平台重置。
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {saving ? '保存中...' : '保存配置'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
